
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import Swal from "sweetalert2";
import { PDFDocument } from "pdf-lib";

export default defineComponent({
  name: "CertificateApproveList",
  setup() {
    const activeTab = ref<"pending" | "history">("pending");
    const searchText = ref("");
    const isLoading = ref(false);
    const showFilter = ref(false);
    const filterCourse = ref("");
    const selectAll = ref(false);
    const totalCount = ref(0);
    const startDate = ref("");
    const endDate = ref("");

    const pendingList = ref<any[]>([]);

    const historyList = ref<any[]>([]);

    const currentList = computed(() =>
      activeTab.value === "pending" ? pendingList.value : historyList.value,
    );

    // client-side filter only for course dropdown (keyword/date handled by API)
    const filteredList = computed(() =>
      !filterCourse.value
        ? currentList.value
        : currentList.value.filter(
            (item) => item.course === filterCourse.value,
          ),
    );

    const pageSize = 10;
    const currentPage = ref(1);
    const totalPages = computed(() => Math.ceil(totalCount.value / pageSize));
    // server already paged — just expose filteredList directly
    const paginatedList = computed(() => filteredList.value);

    const visiblePages = computed(() => {
      const total = totalPages.value;
      const cur = currentPage.value;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages: (number | "...")[] = [];
      // always show first 2
      pages.push(1, 2);
      const left = Math.max(3, cur - 1);
      const right = Math.min(total - 2, cur + 1);
      if (left > 3) pages.push("...");
      for (let i = left; i <= right; i++) pages.push(i);
      if (right < total - 2) pages.push("...");
      // always show last 2
      pages.push(total - 1, total);
      return pages;
    });

    const toggleSelectAll = () => {
      filteredList.value.forEach((item) => (item.selected = selectAll.value));
    };

    const updateSelectAll = () => {
      selectAll.value =
        filteredList.value.length > 0 &&
        filteredList.value.every((item) => item.selected);
    };

    const clearSelection = () => {
      filteredList.value.forEach((item) => (item.selected = false));
      selectAll.value = false;
    };

    const router = useRouter();

    const logout = () => {
      localStorage.removeItem("u_id");
      localStorage.removeItem("id_token");
      localStorage.removeItem("adminRole");
      localStorage.removeItem("userInfo");
      router.push({ name: "sign-in" });
    };

    const token = () => localStorage.getItem("id_token");

    const getSyllabusRegister = async () => {
      isLoading.value = true;
      try {
        const isPending = activeTab.value === "pending";
        const params: Record<string, number | string | undefined> = {
          certStatus: isPending ? 1 : 2,
          status: isPending ? 4 : 0,
          sizeContents: pageSize,
          page: currentPage.value - 1,
          keyWord: searchText.value || undefined,
          orderBy: 2,
        };
        if (isPending) {
          params.finishStartDate = startDate.value
            ? new Date(startDate.value).getTime()
            : undefined;
          params.finishEndDate = endDate.value
            ? new Date(endDate.value).getTime()
            : undefined;
        } else {
          params.approveStartDate = startDate.value
            ? new Date(startDate.value).getTime()
            : undefined;
          params.approveEndDate = endDate.value
            ? new Date(endDate.value).getTime()
            : undefined;
        }
        const res = await axios.get(
          process.env.VUE_APP_API_URL + "/getSyllabusRegister",
          { params, headers: { token: token() } },
        );
        const content: any[] = res.data?.data?.content ?? [];
        totalCount.value = res.data?.data?.totalElements ?? content.length;

        const enriched: any[] = [];
        for (const item of content) {
          // Step 1: getExamScore
          let examScore: any = null;
          try {
            const scoreRes = await axios.get(
              process.env.VUE_APP_API_URL + "/getExamScore",
              {
                params: { syllabusRegisterId: item.id, examType: 0 },
                headers: { token: token() },
              },
            );
            const scores: any[] = scoreRes.data?.data?.content ?? [];
            examScore = scores.length > 0 ? scores[0] : null;
          } catch { /* ไม่มี score */ }

          // Step 2: getExamRoomRegister
          let examRoomRegister: any = null;
          try {
            const examRoomRegRes = await axios.get(
              process.env.VUE_APP_API_URL + "/getExamRoomRegister",
              {
                params: { userId: item.userId },
                headers: { token: token() },
              },
            );
            const examRoomRegisters: any[] = examRoomRegRes.data?.data?.content ?? [];
            examRoomRegister =
              examRoomRegisters.find(
                (r: any) => r.examRoomProfile?.syllabusId === item.syllabusId,
              ) ?? null;
          } catch { /* ไม่มี examRoomRegister */ }

          // Step 3: getExamCenter (ถ้ามี examCenterId จาก examRoomProfile)
          let examCenterProfile: any = null;
          const examCenterId = examRoomRegister?.examRoomProfile?.examCenterId;
          if (examCenterId) {
            try {
              const examCenterRes = await axios.get(
                process.env.VUE_APP_API_URL + "/getExamCenter",
                {
                  params: { id: examCenterId },
                  headers: { token: token() },
                },
              );
              examCenterProfile = examCenterRes.data?.data?.content?.[0] ?? null;
            } catch { /* ไม่มี examCenter */ }
          }

          enriched.push({
            ...item,
            examRoomRegister,
            examCenterProfile,
            examScore,
          });
        }

        if (isPending) {
          pendingList.value = enriched;
        } else {
          historyList.value = enriched;
        }
        console.log(enriched);
      } catch (e) {
        console.error("getSyllabusRegister error", e);
      } finally {
        isLoading.value = false;
      }
    };

    const applyFilter = () => {
      showFilter.value = false;
      currentPage.value = 1;
      getSyllabusRegister();
    };

    const approveSelected = () => {
      const selected = filteredList.value.filter((i) => i.selected);
      if (selected.length === 0) {
        Swal.fire({
          title: "กรุณาเลือกรายการ",
          icon: "warning",
          confirmButtonText: "ตกลง",
        });
        return;
      }
      Swal.fire({
        title: `อนุมัติ ${selected.length} รายการ?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "อนุมัติ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#323563",
      }).then(async (result: { isConfirmed: boolean }) => {
        if (result.isConfirmed) {
          // console.log("อนุมัติ:" +selected);

          try {
            await Promise.all(
              selected.map((item) =>
                axios.post(
                  process.env.VUE_APP_API_URL +
                    "/manageSyllabusCertificateApprove" +
                    "?id=" +
                    item.id +
                    "&status=2" +
                    "&cerNo=" +
                    (item.cerNo != null ? item.cerNo : ""),
                  null,
                  { headers: { token: token() } },
                ),
              ),
            );
            Swal.fire({
              title: "อนุมัติสำเร็จ",
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            clearSelection();
            getSyllabusRegister();
          } catch {
            Swal.fire({
              title: "เกิดข้อผิดพลาด",
              icon: "error",
              confirmButtonText: "ตกลง",
            });
          }
        }
      });
    };

    const downloadSelected = async () => {
      const selected = filteredList.value.filter((i) => i.selected);
      if (selected.length === 0) {
        Swal.fire({
          title: "กรุณาเลือกรายการ",
          icon: "warning",
          confirmButtonText: "ตกลง",
        });
        return;
      }

      Swal.fire("กำลังดาวน์โหลด...", "", "info");

      try {
        // ดาวน์โหลด PDF ทุกรายการพร้อมกัน
        const blobs = await Promise.all(
          selected.map(async (item) => {
            const res = await axios.get(
              process.env.VUE_APP_API_URL +
                "/downloadCertificateSyllabus?syllabusRegisterId=" +
                item.id,
              {
                headers: { token: token() },
                responseType: "blob",
              },
            );
            // ถ้า server ส่ง JSON error กลับมาในรูป blob
            if (res.data.type === "application/json") {
              const text = await (res.data as Blob).text();
              throw new Error(JSON.parse(text).message ?? "ดาวน์โหลดไม่สำเร็จ");
            }
            return res.data as Blob;
          }),
        );

        // Merge ทุก PDF เข้าด้วยกัน
        const merged = await PDFDocument.create();
        for (const blob of blobs) {
          const arrayBuffer = await blob.arrayBuffer();
          const pdf = await PDFDocument.load(arrayBuffer);
          const pages = await merged.copyPages(pdf, pdf.getPageIndices());
          pages.forEach((page) => merged.addPage(page));
        }

        const mergedBytes = await merged.save();
        const blob = new Blob(
          [new Uint8Array(mergedBytes).buffer as ArrayBuffer],
          { type: "application/pdf" },
        );
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "certificates.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        Swal.close();
      } catch (error) {
        const msg =
          error instanceof Error ? error.message : "กรุณาลองใหม่อีกครั้ง";
        Swal.fire({
          title: "ดาวน์โหลดไฟล์ไม่สำเร็จ",
          text: msg,
          icon: "error",
          confirmButtonText: "ตกลง",
        });
      }
    };

    const formatDate = (date: Date | null) => {
      if (!date) return "-";
      return (
        new Date(date).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }) +
        " " +
        new Date(date).toLocaleTimeString("th-TH", {
          hour: "2-digit",
          minute: "2-digit",
        }) +
        " น."
      );
    };

    const expandedIds = ref<Set<number>>(new Set());
    const toggleExpand = (id: number) => {
      const next = new Set(expandedIds.value);
      next.has(id) ? next.delete(id) : next.add(id);
      expandedIds.value = next;
    };
    onMounted(async () => {
      try {
        const res = await axios.get(
          process.env.VUE_APP_API_URL +
            "/getProfile?id=" +
            localStorage.getItem("u_id"),
          { headers: { token: localStorage.getItem("id_token") } },
        );
        if (res.data?.data?.adminRole !== 4) {
          router.push({ name: "404" });
          return;
        }
      } catch {
        router.push({ name: "sign-in" });
        return;
      }

      await getSyllabusRegister();
    });

    watch(activeTab, () => {
      currentPage.value = 1;
      getSyllabusRegister();
    });

    watch(searchText, () => {
      currentPage.value = 1;
      getSyllabusRegister();
    });

    watch(currentPage, () => {
      getSyllabusRegister();
    });

    return {
      activeTab,
      searchText,
      showFilter,
      filterCourse,
      selectAll,
      totalCount,
      filteredList,
      toggleSelectAll,
      updateSelectAll,
      clearSelection,
      applyFilter,
      approveSelected,
      downloadSelected,
      formatDate,
      logout,
      expandedIds,
      toggleExpand,
      currentPage,
      totalPages,
      visiblePages,
      paginatedList,
      startDate,
      endDate,
      getSyllabusRegister,
      isLoading,
    };
  },
});
